import { encodeQuery, parseQuery } from "../core/utilities";
import nanoid from "nanoid";

const DEFAULTS = {
  token_type: "JWT",
  response_type: "id_token",
  tokenName: "Authorization",
  scope: ["openid"]
};

export default class OIDCScheme {
  constructor(auth, options) {
    this.$auth = auth;
    this.req = auth.ctx.req;
    this.name = options._name;

    this.options = Object.assign({}, DEFAULTS, options);
  }

  async getDiscoveryData() {
    this.discoveryData = this.$auth.$storage.getUniversal(
      this.name + this.options.issuer
    );

    if (!this.discoveryData) {
      const discoveryUri = `${this.options.issuer}/.well-known/openid-configuration`;

      this.discoveryData = await this.$auth.ctx.$axios.$get(discoveryUri);

      this.$auth.$storage.setUniversal(
        this.name + this.options.issuer,
        this.discoveryData
      );
    }
  }

  get _scope() {
    return Array.isArray(this.options.scope)
      ? this.options.scope.join(" ")
      : this.options.scope;
  }

  get _redirectURI() {
    //eslint-disable-line
    const url = this.options.redirect_uri;

    if (url) {
      return url;
    }

    return window.location.origin + this.options.callback;
  }

  async mounted() {
    // Sync token
    const token = this.$auth.syncToken(this.name);
    // Set axios token
    if (token) {
      this._setToken(token);
    }

    // Handle callbacks on page load
    const { redirected, OPSessionCheckedOnce } = await this._handleCallback();

    OPSessionCheckedOnce &&
      this.$auth.$storage.setUniversal("OPSessionCheckedOnce", true);

    if (!redirected) {
      this.$auth.$storage.syncUniversal("OPSessionCheckedOnce");
      this.$auth.$storage.syncUniversal("check_session_iframe");
      this.$auth.$storage.syncUniversal("client_id");
      this.$auth.$storage.syncUniversal("identity_provider");
      return this.$auth.fetchUserOnce();
    }
  }

  _setToken(token) {
    // Set Authorization token for all axios requests
    this.$auth.ctx.$axios.setHeader(this.options.tokenName, token);
  }

  _clearToken() {
    // Clear Authorization token for all axios requests
    this.$auth.ctx.$axios.setHeader(this.options.tokenName, false);
  }

  async logout() {
    await this.getDiscoveryData();
    let nextOpts = {};
    nextOpts.logoutState = nanoid();
    this.$auth.$storage.setUniversal(
      this.name + ".logoutState",
      nextOpts.logoutState
    );

    let opts = {};
    opts.next = window.location.href + "?" + encodeQuery(nextOpts);

    window.location = `${this.discoveryData.end_session_endpoint}?${encodeQuery(
      opts
    )}`;
  }

  async rpLogout() {
    this._clearToken();

    if (this.$auth.options.autoLoginOIDCStrategy) {
      this.$auth.$storage.removeUniversal("OPSessionCheckedOnce");
    } else {
      this.$auth.$storage.removeUniversal("check_session_iframe");
      this.$auth.$storage.removeUniversal("client_id");
      this.$auth.$storage.removeUniversal("identity_provider");
    }
    this.$auth.$storage.removeUniversal("oidc_session_state");
    return this.$auth.reset();
  }

  async login({ params, state, nonce } = {}) {
    await this.getDiscoveryData();

    if (!this.discoveryData) {
      throw new Error("Cannot get oicd configuration from discovery url");
    }

    const opts = {
      response_type: this.options.response_type,
      access_type: this.options.access_type,
      client_id: this.options.client_id,
      redirect_uri: this._redirectURI,
      scope: this._scope,
      // Note: The primary reason for using the state parameter is to mitigate CSRF attacks.
      // https://auth0.com/docs/protocols/oauth2/oauth-state
      state: state || nanoid(),
      ...params
    };

    if (this.options.audience) {
      opts.audience = this.options.audience;
    }

    // Set Nonce Value if response_type contains id_token to mitigate Replay Attacks
    // More Info: https://openid.net/specs/openid-connect-core-1_0.html#NonceNotes
    // More Info: https://tools.ietf.org/html/draft-ietf-oauth-v2-threatmodel-06#section-4.6.2
    if (opts.response_type.includes("id_token")) {
      // nanoid auto-generates an URL Friendly, unique Cryptographic string
      // Recommended by Auth0 on https://auth0.com/docs/api-auth/tutorials/nonce
      opts.nonce = nonce || nanoid();
      this.$auth.$storage.setUniversal(this.name + ".nonce", opts.nonce);
    }

    this.$auth.$storage.setUniversal(this.name + ".state", opts.state);

    const url =
      this.discoveryData.authorization_endpoint + "?" + encodeQuery(opts);

    window.location = url;
  }

  // async fetchUser() {
  //   let strategy = this.$auth.strategy.name;

  //   let token = this.$auth.getToken(strategy);

  //   if (token) {
  //     let jwtToken = token.split(" ")[1];
  //     let user = this._parseJwt(jwtToken);
  //     this.$auth.setUser(user);
  //   }
  // }

  async fetchUser() {
    if (!this.$auth.getToken(this.name)) {
      return;
    }

    if (!this.options.userinfo_endpoint) {
      this.$auth.setUser({});
      return;
    }

    const user = await this.$auth.requestWith(this.name, {
      url: this.options.userinfo_endpoint
    });
    this.$auth.setUser(user);
  }

  _parseJwt(token) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function(c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  }

  silentLogin() {
    this.$auth.loginWith(this.$auth.options.autoLoginOIDCStrategy, {
      params: {
        prompt: "none"
      }
    });
  }

  async setAutoLoginUniversal() {
    await this.getDiscoveryData();
    this.$auth.$storage.setUniversal(
      "check_session_iframe",
      this.discoveryData.check_session_iframe
    );

    this.$auth.$storage.setUniversal("client_id", this.options.client_id);

    this.$auth.$storage.setUniversal("identity_provider", this.options.issuer);
    return;
  }

  async _handleCallback() {
    //eslint-disable-line
    await this.getDiscoveryData();

    const hash = parseQuery(this.$auth.ctx.route.hash.substr(1));

    const parsedQuery = Object.assign({}, this.$auth.ctx.route.query, hash);

    if (
      parsedQuery["logoutState"] &&
      parsedQuery["logoutState"] ===
        this.$auth.$storage.getUniversal(this.name + ".logoutState")
    ) {
      this.rpLogout();
      return {
        redirected: false,
        OPSessionCheckedOnce: false
      };
    }

    // Handle callback only for specified route
    if (this.$auth.ctx.route.path !== this.options.callback) {
      return {
        redirected: false,
        OPSessionCheckedOnce: false
      };
    }

    // Start Handle Error
    if (parsedQuery["error"]) {
      console.error(`OIDC error message: ${parsedQuery["error"]}`); //eslint-disable-line
      return {
        redirected: false,
        OPSessionCheckedOnce: true
      };
      // this.$auth.redirect("home");
    }
    // Start Handle Error

    // accessToken/idToken
    let token = parsedQuery[this.options.token_key || "access_token"];
    // refresh token
    let refreshToken =
      parsedQuery[this.options.refresh_token_key || "refresh_token"];

    let sessionState = parsedQuery.session_state;

    if (sessionState && this.discoveryData.check_session_iframe) {
      this.$auth.$storage.setUniversal("oidc_session_state", sessionState);
      this.setAutoLoginUniversal();
    }

    // Validate state
    const state = this.$auth.$storage.getUniversal(this.name + ".state");
    this.$auth.$storage.setUniversal(this.name + ".state", null);
    if (state && parsedQuery.state !== state) {
      return {
        redirected: false,
        OPSessionCheckedOnce: true
      };
    }

    let payload = this._parseJwt(token);

    // Validate nonce
    if (
      this.options.response_type.includes("id_token") &&
      this.options.token_key === "id_token"
    ) {
      const nonce = this.$auth.$storage.getUniversal(this.name + ".nonce");
      this.$auth.$storage.setUniversal(this.name + ".nonce", null);

      if (nonce && payload.nonce !== nonce) {
        return {
          redirected: false,
          OPSessionCheckedOnce: true
        };
      }
    }

    // -- Authorization Code Grant --
    if (this.options.response_type === "code" && parsedQuery.code) {
      const data = await this.$auth.request({
        method: "post",
        url: this.options.access_token_endpoint,
        baseURL: false,
        data: encodeQuery({
          code: parsedQuery.code,
          client_id: this.options.client_id,
          redirect_uri: this._redirectURI,
          response_type: this.options.response_type,
          audience: this.options.audience,
          grant_type: this.options.grant_type
        })
      });

      if (data.access_token) {
        token = data.access_token;
      }

      if (data.refresh_token) {
        refreshToken = data.refresh_token;
      }
    }

    if (!token || !token.length) {
      return {
        redirected: false,
        OPSessionCheckedOnce: true
      };
    }

    // Append token_type
    if (this.options.token_type) {
      token = this.options.token_type + " " + token;
    }

    // Store token
    this.$auth.setToken(this.name, token);

    // Set axios token
    this._setToken(token);

    // Store refresh token
    if (refreshToken && refreshToken.length) {
      refreshToken = this.options.token_type + " " + refreshToken;
      this.$auth.setRefreshToken(this.name, refreshToken);
    }

    // Redirect to home
    this.$auth.redirect("home", true);

    return {
      redirected: false,
      OPSessionCheckedOnce: true
    }; // True means a redirect happened
  }
}
